import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, Gauge, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useHardware } from "../context/HardwareContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

export function Stars({ value, onPick, size = "h-4 w-4", testid }) {
  return (
    <span className="inline-flex gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} onClick={onPick ? () => onPick(n) : undefined} data-testid={onPick ? `review-star-${n}` : undefined}
          className={`${size} ${n <= Math.round(value || 0) ? "fill-amber-400 text-amber-400" : "text-slate-600"} ${onPick ? "cursor-pointer" : ""}`} />
      ))}
    </span>
  );
}

export const hwSummary = (hw) =>
  `${hw.gpu || "GPU ?"}${(hw.gpuCount || 1) > 1 && !/^\d+\s*×/.test(hw.gpu || "") ? ` ×${hw.gpuCount}` : ""} · ${hw.vram} Go VRAM · ${hw.ram} Go RAM`.slice(0, 120);

function ReviewForm({ model, onSaved }) {
  const { hardware } = useHardware();
  const [rating, setRating] = useState(0);
  const [tok, setTok] = useState("");
  const [hw, setHw] = useState(hwSummary(hardware));
  const [comment, setComment] = useState("");
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/reviews/mine/${model.id}`).then(({ data }) => {
      const r = data.review;
      if (!r) return;
      setExists(true);
      setRating(r.rating);
      setTok(r.tok_per_sec ?? "");
      setHw(r.hardware);
      setComment(r.comment);
    }).catch(() => {});
  }, [model.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Choisissez une note de 1 à 5 étoiles");
    setBusy(true);
    try {
      await api.put(`/reviews/model/${model.id}`, { rating, tok_per_sec: tok === "" ? null : Number(tok), hardware: hw, comment });
      setExists(true);
      toast.success("Merci, votre avis est publié");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'envoi");
    }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/reviews/model/${model.id}`);
      setExists(false);
      setRating(0);
      setTok("");
      setComment("");
      toast("Avis supprimé");
      onSaved();
    } catch {
      toast.error("Suppression impossible");
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-[#1E2638] bg-[#080B10] p-4" data-testid="review-form">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{exists ? "Modifier mon avis" : "Vous l'avez testé ? Donnez votre avis"}</span>
        <Stars value={rating} onPick={setRating} size="h-5 w-5" testid="review-rating-input" />
      </div>
      <div className="grid gap-2 sm:grid-cols-[140px_1fr]">
        <Input type="number" min="0" max="2000" step="0.1" value={tok} onChange={(e) => setTok(e.target.value)} placeholder="tok/s obtenus" data-testid="review-tok-input" className="text-xs" />
        <Input value={hw} maxLength={120} onChange={(e) => setHw(e.target.value)} placeholder="Votre machine" data-testid="review-hardware-input" className="text-xs" />
      </div>
      <Textarea value={comment} maxLength={500} onChange={(e) => setComment(e.target.value)} placeholder="Votre retour (facultatif, 500 caractères max)" data-testid="review-comment-input" className="min-h-[70px] text-xs" />
      <div className="flex items-center justify-end gap-2">
        {exists && (
          <Button type="button" variant="outline" size="sm" onClick={remove} disabled={busy} data-testid="review-delete-button" className="gap-1 text-red-400">
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </Button>
        )}
        <Button type="submit" size="sm" disabled={busy} data-testid="review-submit-button" className="gap-1.5">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {exists ? "Mettre à jour" : "Publier"}
        </Button>
      </div>
    </form>
  );
}

export function ModelReviews({ model }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["reviews", model.id],
    queryFn: async () => (await api.get(`/reviews/model/${model.id}`)).data,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["reviews", model.id] });
    qc.invalidateQueries({ queryKey: ["review-summary"] });
  };

  return (
    <div className="space-y-4" data-testid="model-reviews">
      <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="model-reviews-stats">
        <Stars value={data?.avg} />
        <span>{data?.count ? `${data.avg}/5 · ${data.count} avis` : "Aucun avis pour l'instant"}</span>
        {data?.median_tok_per_sec && <span className="font-mono2 text-xs text-muted-foreground"><Gauge className="inline h-3 w-3" /> médiane {data.median_tok_per_sec} tok/s</span>}
      </div>
      {user ? <ReviewForm model={model} onSaved={refresh} /> : (
        <p className="text-xs text-muted-foreground" data-testid="review-login-hint"><Link to="/login" className="text-primary hover:underline">Connectez-vous</Link> pour noter ce modèle.</p>
      )}
      {isLoading ? <p className="text-xs text-muted-foreground">Chargement…</p> : (
        <ul className="space-y-2" data-testid="review-list">
          {(data?.reviews || []).map((r) => (
            <li key={r.id} data-testid="review-item" className="rounded-lg border border-[#1E2638] p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Stars value={r.rating} size="h-3.5 w-3.5" />
                <span className="font-mono2 text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString("fr-FR")}</span>
              </div>
              <p className="mt-1 font-mono2 text-[11px] text-slate-400">{r.hardware || "Machine non précisée"}{r.tok_per_sec ? ` · ${r.tok_per_sec} tok/s` : ""}</p>
              {r.comment && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-300">{r.comment}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
